import { formatSlot, nextAnswer, type BallotDTO, type PollAnswer } from '@kartothek/shared';
import { useEffect, useRef, useState } from 'react';
import { ApiError } from '../api';
import { t, tDyn } from '../i18n';
import { classNames } from '../util';

type Mine = BallotDTO['mine'];

/**
 * Abstimmen für eine Person: ein Tipp pro Termin wechselt Ja → Vielleicht → Nein,
 * optional mit Kommentar. Gespeichert wird sofort (smartphonetauglich).
 */
export function Ballot({
  ballot,
  onVote,
  icsUrl,
  sessionLink,
}: {
  ballot: BallotDTO;
  onVote: (slotId: string, answer: PollAnswer, comment: string) => Promise<BallotDTO>;
  icsUrl?: string;
  sessionLink?: React.ReactNode;
}) {
  const [mine, setMine] = useState<Mine>(ballot.mine);
  const [slots, setSlots] = useState(ballot.slots);
  const [commentOpen, setCommentOpen] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(0);
  // Schnelles Mehrfach-Tippen: Anfragen nacheinander senden, lokaler Stand bleibt maßgeblich
  const local = useRef<Mine>(ballot.mine);
  const confirmed = useRef<Mine>(ballot.mine);
  const queue = useRef<Promise<void>>(Promise.resolve());
  const pending = useRef(0);

  useEffect(() => {
    if (pending.current > 0) return;
    local.current = confirmed.current = ballot.mine;
    setMine(ballot.mine);
    setSlots(ballot.slots);
  }, [ballot]);

  const save = (slotId: string, answer: PollAnswer, comment: string) => {
    local.current = { ...local.current, [slotId]: { answer, comment } };
    setMine(local.current);
    setError(null);
    setSaving(++pending.current);
    queue.current = queue.current.then(async () => {
      try {
        const res = await onVote(slotId, answer, comment);
        confirmed.current = res.mine;
        setSlots(res.slots);
        if (pending.current === 1) {
          local.current = res.mine;
          setMine(res.mine);
        }
      } catch (e) {
        // Diesen Termin auf den zuletzt gespeicherten Stand zurücksetzen
        const next = { ...local.current };
        const before = confirmed.current[slotId];
        if (before) next[slotId] = before;
        else delete next[slotId];
        local.current = next;
        setMine(next);
        setError(e instanceof ApiError ? e.message : String(e));
      } finally {
        setSaving(--pending.current);
      }
    });
  };

  const final = ballot.final;
  return (
    <div className="ballot">
      {ballot.description && <p className="ballot-desc">{ballot.description}</p>}
      {ballot.locationHint && (
        <p className="muted">
          {t('polls.location')}: {ballot.locationHint}
        </p>
      )}
      {final && (
        <div className="ballot-final">
          <strong>{t('polls.fixedOn', { slot: formatSlot(final.startAt, final.endAt) })}</strong>
          <div className="row">
            {icsUrl && (
              <a className="button primary" href={icsUrl} download>
                {t('polls.ics')}
              </a>
            )}
            {sessionLink}
          </div>
        </div>
      )}
      {ballot.status === 'cancelled' && <p className="notice-inline">{t('polls.cancelledNotice')}</p>}
      {!final && ballot.status !== 'cancelled' && (
        <p className="small muted">
          {ballot.canVote ? (
            <>
              {t('polls.tapHint')} {t('polls.savedHint')}
              {ballot.deadline && <> · {t('polls.deadlineNotice', { date: formatSlot(ballot.deadline) })}</>}
            </>
          ) : (
            t('polls.closedNotice')
          )}
        </p>
      )}
      <ul className="ballot-list">
        {slots.map((s) => {
          const my = mine[s.id];
          const isFinal = final?.id === s.id;
          return (
            <li key={s.id} className={classNames('ballot-slot', isFinal && 'final')}>
              <button
                type="button"
                className={classNames('ballot-tap', my && `poll-${my.answer}`)}
                disabled={!ballot.canVote}
                onClick={() => save(s.id, nextAnswer(my?.answer), my?.comment ?? '')}
                aria-label={`${formatSlot(s.startAt, s.endAt)}: ${tDyn(`polls.answer.${my?.answer ?? 'none'}`)}`}
              >
                <span className="ballot-when">{formatSlot(s.startAt, s.endAt)}</span>
                <span className="ballot-answer">{tDyn(`polls.answer.${my?.answer ?? 'none'}`)}</span>
              </button>
              <div className="ballot-meta small muted">
                <span>{t('polls.othersSum', { yes: s.yes, maybe: s.maybe })}</span>
                {ballot.canVote && (
                  <button type="button" className="link-button" onClick={() => setCommentOpen(commentOpen === s.id ? null : s.id)}>
                    {my?.comment ? `„${my.comment}“` : t('polls.comment')}
                  </button>
                )}
                {!ballot.canVote && my?.comment && <span>„{my.comment}“</span>}
              </div>
              {commentOpen === s.id && ballot.canVote && (
                <CommentInput
                  label={t('polls.commentFor', { slot: formatSlot(s.startAt, null, { year: false }) })}
                  initial={my?.comment ?? ''}
                  onSave={(c) => {
                    setCommentOpen(null);
                    save(s.id, my?.answer ?? 'yes', c);
                  }}
                />
              )}
            </li>
          );
        })}
      </ul>
      <div className="small muted save-status">{saving > 0 ? t('common.saving') : ''}</div>
      {error && <div className="error">{error}</div>}
    </div>
  );
}

function CommentInput({ label, initial, onSave }: { label: string; initial: string; onSave: (c: string) => void }) {
  const [value, setValue] = useState(initial);
  return (
    <form
      className="row ballot-comment"
      onSubmit={(e) => {
        e.preventDefault();
        onSave(value.trim());
      }}
    >
      <input aria-label={label} value={value} maxLength={300} autoFocus onChange={(e) => setValue(e.target.value)} />
      <button className="primary">{t('common.save')}</button>
    </form>
  );
}
